import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { subscriptionSlice, userSlice } from '../reducers'
import useAccountInfoRequest from './useAccountInfoRequest'

// Far enough out that a connection still coming up on a first login has
// settled by then.
export const RETRY_DELAY_MS = 1500

// Automatic retries after a failure. Anything past this is the user's call,
// through the billing card's "Try again". The count lives in the store, so
// every mount of this hook agrees on it and a cancelled timer costs nothing.
export const MAX_AUTO_RETRIES = 1

/**
 * Keeps the account info fetched for as long as something that needs it is
 * mounted: the paid-consumer toolbar (every paid page), the Account settings
 * layout (every section, so the drawer's Subscription & billing entry does not
 * depend on the billing page happening to be open) and PaymentProfile.
 *
 * The request and its retry live together. The event bus queues a trigger
 * sent before the socket reports itself ready and times it out after 30s,
 * which on a cold first login is how a perfectly good account used to end up
 * stored as "loaded, empty" with nothing ever asking again. When a fetch
 * fails, this asks once more, past the cache, wherever the user happens to
 * be — the trial banner, Gleap and the student-plan check are fed by the same
 * data, not only the profile page.
 *
 * Whether to send at all — tenant, signed-in profile, a request already out —
 * is decided in `useAccountInfoRequest`, once, off the store.
 */
const useEnsureAccountInfo = () => {
  const username = useSelector((state) => userSlice.selectors.selectProfile(state)?.username)
  const accountInfoIdle = useSelector(subscriptionSlice.selectors.selectAccountInfoIdle)
  const accountInfoError = useSelector(subscriptionSlice.selectors.selectAccountInfoError)
  const accountInfoAttempts = useSelector(subscriptionSlice.selectors.selectAccountInfoAttempts)
  const requestAccountInfo = useAccountInfoRequest()

  // `username` is a dependency, not a guard: the request is dropped until the
  // profile lands, and has to be sent again once it does.
  useEffect(() => {
    if (!accountInfoIdle || !username) return
    requestAccountInfo()
  }, [accountInfoIdle, username, requestAccountInfo])

  // The attempt count is what has been sent since the last success, so the
  // first failure is attempt 1. Several mounts may each start a timer; the
  // first to fire sends, the rest find the request in flight and do nothing.
  useEffect(() => {
    if (!accountInfoError || accountInfoAttempts > MAX_AUTO_RETRIES) return undefined
    const timer = setTimeout(
      () => requestAccountInfo({ invalidateCache: true }),
      RETRY_DELAY_MS
    )
    return () => clearTimeout(timer)
  }, [accountInfoError, accountInfoAttempts, requestAccountInfo])
}

export default useEnsureAccountInfo
